import { call } from "redux-saga/effects";
import action from "../../decorators/action.decorator";
import reducer, { InitSchemaReducer } from "../../decorators/reducer.decorator";
import { DIContainerKeys } from "../../di-container/di-container.keys";
import { Entities } from "../../store/entities/entities.enum";
import { HttpService } from "../http.service";
import { ReducerMethods } from "../../store/reducer.methods";
import { API_DOMENS } from "../../../constants/apis.constants";
import loader from "../../decorators/loader.decorator";
import { Loaders } from "../../store/loaders/loaders.enum";
import { CallToast } from "../../../Components/PriceCalculator/modals/CustomToast";
import type AddIPAddressDto from "./dto/add-ip-address.dto";
import type UpdateIPAddressDto from "./dto/update-ip-address.dto";
import type DeleteIPAddressDto from "./dto/delete-ip-address.dto";
import type GetIPAddressByIdDto from "./dto/get-ip-address-by-id.dto";

export const NetworkAccessEffectActions = {
  getAllIPAddresses: `${Entities.NetworkAccess}_getAllIPAddresses`,
  getIPAddressById: `${Entities.NetworkAccess}_getIPAddressById`,
  addIPAddress: `${Entities.NetworkAccess}_addIPAddress`,
  updateIPAddress: `${Entities.NetworkAccess}_updateIPAddress`,
  deleteIPAddress: `${Entities.NetworkAccess}_deleteIPAddress`,
};

const initSchema: InitSchemaReducer = {
  definitions: {  },
  options: { idAttribute: '' },
};

@reducer({
  entityName: Entities.NetworkAccess,
  initSchema,
  aliasName: DIContainerKeys.NetworkAccessService,
})
export class NetworkAccessService extends HttpService {
  @loader(Loaders.GetAllIPAddressesLoader)
  @action(DIContainerKeys.NetworkAccessService)
  public *getAllIPAddresses(): Generator<any, void, any> {
    yield this.setLoaderState(Loaders.GetAllIPAddressesLoader, true);
    try {
      const response = yield call(
        this.get,
        {
          url: "GetAllIPAddresses",
          domain: API_DOMENS.IPWhitelist_API,
        },
        ReducerMethods.UPDATE
      );
      
      if (response instanceof Error) {
        CallToast("Something went wrong", false, false, "v2style");
        yield this.setLoaderState(Loaders.GetAllIPAddressesLoader, false);
        return;
      }
      
      if (!response?.Success) {
        CallToast(response?.Message || "Something went wrong", false, false, "v2style");
        yield this.setLoaderState(Loaders.GetAllIPAddressesLoader, false);
        return;
      }
      
      yield this.setEntityState(Entities.NetworkAccess, response);
      yield this.setLoaderState(Loaders.GetAllIPAddressesLoader, false);
    } catch (error) {
      CallToast("Something went wrong", false, false, "v2style");
      yield this.setLoaderState(Loaders.GetAllIPAddressesLoader, false);
    }
  }

  @loader(Loaders.GetIPAddressByIdLoader)
  @action(DIContainerKeys.NetworkAccessService)
  public *getIPAddressById(dto: GetIPAddressByIdDto): Generator<any, void, any> {
    yield this.setLoaderState(Loaders.GetIPAddressByIdLoader, true);
    try {
      const response = yield call(
        this.get,
        {
          url: "GetIPAddressById",
          domain: API_DOMENS.IPWhitelist_API,
          params: {
            ipId: dto.ipId,
          },
        },
        ReducerMethods.UPDATE
      );
      
      if (response instanceof Error) {
        CallToast("Something went wrong", false, false, "v2style");
        yield this.setLoaderState(Loaders.GetIPAddressByIdLoader, false);
        return;
      }
      
      if (!response?.Success) {
        CallToast(response?.Message || "Something went wrong", false, false, "v2style");
        yield this.setLoaderState(Loaders.GetIPAddressByIdLoader, false);
        return;
      }
      
      yield this.setEntityState(Entities.NetworkAccess, response);
      yield this.setLoaderState(Loaders.GetIPAddressByIdLoader, false);
    } catch (error) {
      CallToast("Something went wrong", false, false, "v2style");
      yield this.setLoaderState(Loaders.GetIPAddressByIdLoader, false);
    }
  }

  @loader(Loaders.AddIPAddressLoader)
  @action(DIContainerKeys.NetworkAccessService)
  public *addIPAddress(dto: AddIPAddressDto): Generator<any, void, any> {
    yield this.setLoaderState(Loaders.AddIPAddressLoader, true);
    try {
      const response = yield call(
        this.post,
        {
          url: "AddIPAddress",
          domain: API_DOMENS.IPWhitelist_API,
          body: {
            IPAddress: dto.IPAddress,
            Description: dto.Description,
            IsActive: dto.IsActive,
          },
        },
        ReducerMethods.UPDATE
      );
      
      if (response instanceof Error) {
        CallToast("Something went wrong", false, false, "v2style");
        yield this.setLoaderState(Loaders.AddIPAddressLoader, false);
        return;
      }
      
      if (!response?.Success) {
        CallToast(response?.Message || "Something went wrong", false, false, "v2style");
        yield this.setLoaderState(Loaders.AddIPAddressLoader, false);
        return;
      }
      
      CallToast(response?.Message || "IP address added successfully", true, false, "v2style");
      
      yield this.setEntityState(Entities.NetworkAccess, response);
      yield this.setLoaderState(Loaders.AddIPAddressLoader, false);
    } catch (error) {
      CallToast("Something went wrong", false, false, "v2style");
      yield this.setLoaderState(Loaders.AddIPAddressLoader, false);
    }
  }

  @loader(Loaders.UpdateIPAddressLoader)
  @action(DIContainerKeys.NetworkAccessService)
  public *updateIPAddress(dto: UpdateIPAddressDto): Generator<any, void, any> {
    yield this.setLoaderState(Loaders.UpdateIPAddressLoader, true);
    try {
      const response = yield call(
        this.put,
        {
          url: "UpdateIPAddress",
          domain: API_DOMENS.IPWhitelist_API,
          body: {
            Id: dto.Id,
            IPAddress: dto.IPAddress,
            Description: dto.Description,
            IsActive: dto.IsActive,
          },
        },
        ReducerMethods.UPDATE
      );
      
      if (response instanceof Error) {
        CallToast("Something went wrong", false, false, "v2style");
        yield this.setLoaderState(Loaders.UpdateIPAddressLoader, false);
        return;
      }
      
      if (!response?.Success) {
        CallToast(response?.Message || "Something went wrong", false, false, "v2style");
        yield this.setLoaderState(Loaders.UpdateIPAddressLoader, false);
        return;
      }
      
      CallToast(response?.Message || "IP address updated successfully", true, false, "v2style");
      
      yield this.setEntityState(Entities.NetworkAccess, response);
      yield this.setLoaderState(Loaders.UpdateIPAddressLoader, false);
    } catch (error) {
      CallToast("Something went wrong", false, false, "v2style");
      yield this.setLoaderState(Loaders.UpdateIPAddressLoader, false);
    }
  }

  @loader(Loaders.DeleteIPAddressLoader)
  @action(DIContainerKeys.NetworkAccessService)
  public *deleteIPAddress(dto: DeleteIPAddressDto): Generator<any, void, any> {
    yield this.setLoaderState(Loaders.DeleteIPAddressLoader, true);
    try {
      const response = yield call(
        this.delete,
        {
          url: "DeleteIPAddress",
          domain: API_DOMENS.IPWhitelist_API,
          params: {
            ipId: dto.ipId,
          },
        },
        ReducerMethods.UPDATE
      );
      
      if (response instanceof Error) {
        if (!dto.suppressToast) {
          CallToast("Something went wrong", false, false, "v2style");
        }
        yield this.setLoaderState(Loaders.DeleteIPAddressLoader, false);
        return;
      }
      
      if (!response?.Success) {
        if (!dto.suppressToast) {
          CallToast(response?.Message || "Something went wrong", false, false, "v2style");
        }
        yield this.setLoaderState(Loaders.DeleteIPAddressLoader, false);
        return;
      }
      
      if (!dto.suppressToast) {
        CallToast(response?.Message || "IP address deleted successfully", true, false, "v2style");
      }
      
      yield this.setEntityState(Entities.NetworkAccess, response);
      yield this.setLoaderState(Loaders.DeleteIPAddressLoader, false);
    } catch (error) {
      if (!dto.suppressToast) {
        CallToast("Something went wrong", false, false, "v2style");
      }
      yield this.setLoaderState(Loaders.DeleteIPAddressLoader, false);
    }
  }
}